import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { mainNav } from "@/mocks/navigation";
import { useStore } from "@/store/StoreContext";
import BrandLogo from "./BrandLogo";
import MobileMenu from "./MobileMenu";

export type PanelKey = "search" | "favorites" | "bag";

interface HeaderProps {
  onOpenPanel: (panel: PanelKey) => void;
  overlay?: boolean;
}

export default function Header({ onOpenPanel, overlay = false }: HeaderProps) {
  const { cartCount, favorites } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const solid = scrolled || menuOpen || !overlay;

  const openPanel = (panel: PanelKey) => {
    setMenuOpen(false);
    onOpenPanel(panel);
  };

  const linkClass = `cursor-pointer whitespace-nowrap text-[13px] tracking-wide transition-colors ${
    solid ? "text-foreground-700 hover:text-primary-600" : "text-foreground-800 hover:text-primary-600"
  }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-500 ${
        solid
          ? "border-b border-background-200 bg-background-50/95 backdrop-blur"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="flex h-16 items-center justify-between gap-4 px-4 md:h-20 md:px-8">
        <Link
          to="/"
          aria-label="Auréa — página inicial"
          className="flex shrink-0 cursor-pointer items-center"
          onClick={() => setMenuOpen(false)}
        >
          <BrandLogo tone="dark" />
        </Link>

        <nav aria-label="Categorias" className="hidden items-center gap-4 xl:flex">
          {mainNav.map((item) =>
            item.children ? (
              <div key={item.label} className="group relative">
                <button
                  type="button"
                  className={`${linkClass} flex items-center gap-1`}
                  aria-haspopup="true"
                >
                  {item.label}
                  <i className="ri-arrow-down-s-line text-sm" />
                </button>
                <div className="invisible absolute left-1/2 top-full z-10 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                  <div className="flex min-w-[170px] flex-col rounded-lg border border-background-200 bg-background-50 p-2">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        className="cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link key={item.label} to={item.href} className={linkClass}>
                {item.label}
              </Link>
            ),
          )}
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          <button
            type="button"
            onClick={() => openPanel("search")}
            aria-label="Buscar"
            className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors ${
              solid ? "text-foreground-700 hover:bg-background-100" : "text-foreground-800 hover:bg-background-200/70"
            }`}
          >
            <i className="ri-search-line text-xl" />
          </button>
          <button
            type="button"
            onClick={() => openPanel("favorites")}
            aria-label="Favoritos"
            className={`relative hidden h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors sm:flex ${
              solid ? "text-foreground-700 hover:bg-background-100" : "text-foreground-800 hover:bg-background-200/70"
            }`}
          >
            <i className="ri-heart-3-line text-xl" />
            {favorites.length > 0 ? (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-[10px] font-medium text-foreground-950">
                {favorites.length}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={() => openPanel("bag")}
            aria-label="Sacola"
            className={`relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors ${
              solid ? "text-foreground-700 hover:bg-background-100" : "text-foreground-800 hover:bg-background-200/70"
            }`}
          >
            <i className="ri-shopping-bag-3-line text-xl" />
            {cartCount > 0 ? (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-[10px] font-medium text-background-50">
                {cartCount}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir menu"
            className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors xl:hidden ${
              solid ? "text-foreground-800 hover:bg-background-100" : "text-foreground-800 hover:bg-background-200/70"
            }`}
          >
            <i className="ri-menu-3-line text-2xl" />
          </button>
        </div>
      </div>

      {menuOpen ? (
        <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} onOpenPanel={onOpenPanel} />
      ) : null}
    </header>
  );
}